import type { Message, ChatContext } from '../../../types/models';
import { formatMessages } from './messageFormatter';
import { APIError } from '../../error/errorHandler';
import { getProviderConfig, getProviderUrl } from '../../../config/modelsDatabase';
import { usageService } from '../../../services/UsageService';
import { useStore } from '../../../store/useStore';

interface AnthropicResponse {
  id: string;
  content: Array<{
    type: string;
    text: string;
  }>;
  model: string;
  role: string;
  usage: {
    input_tokens: number;
    output_tokens: number;
  };
}

interface ErrorResponse {
  error: string | {
    message: string;
    [key: string]: any;
  };
}

export async function sendMessage(
  message: string,
  apiKey: string,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[]
): Promise<string> {
  try {
    // Get Anthropic configuration
    const config = getProviderConfig('anthropic');
    if (!config) {
      throw new Error('Anthropic provider configuration not found');
    }

    // Format messages for Anthropic's API
    const { messages, system } = formatMessages(message, context, previousMessages);

    // Make the API request through our Cloudflare Worker
    console.log('Sending request to Anthropic proxy:', {
      messages: messages.length,
      hasSystem: !!system,
      modelId,
      apiKeyPresent: !!apiKey
    });

    const response = await fetch('https://anthropic-proxy.camaleon.ai/api/anthropic-chat', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'x-beta-feature': 'output-128k-2025-02-19'
      },
      body: JSON.stringify({
        model: modelId,
        messages,
        system: system || 'You are a helpful AI assistant.',
        max_tokens: modelId.includes('claude-3') ? 8192 : 4096
      })
    });

    // First check if response is ok
    if (!response.ok) {
      // Try to get error details from response
      let errorMessage = `HTTP Error ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json() as ErrorResponse;
        if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || errorMessage;
        }
      } catch (parseError: any) {
        // If we can't parse the error response, add that info to the error message
        errorMessage += ` (Failed to parse error response: ${parseError.message})`;
      }

      // Handle specific CORS errors
      if (response.status === 0) {
        throw new Error('CORS Error: Unable to access the Anthropic proxy. Please check if your origin is allowed.');
      }
      if (response.status === 403) {
        throw new Error('CORS Error: Access forbidden. Your origin may not be whitelisted.');
      }
      if (response.status === 502) {
        throw new Error('Proxy Error: Unable to reach Anthropic API. The proxy service may be experiencing issues.');
      }

      throw new Error(errorMessage);
    }

    // Try to parse the successful response
    let responseText;
    try {
      responseText = await response.text();
      if (!responseText) {
        throw new Error('Empty response from server');
      }
    } catch (parseError: any) {
      throw new Error(`Failed to read response: ${parseError.message}`);
    }

    // Try to parse the response as JSON
    let data;
    try {
      data = JSON.parse(responseText) as AnthropicResponse;
    } catch (parseError: any) {
      throw new Error(`Failed to parse JSON response: ${parseError.message}\nResponse text: ${responseText}`);
    }

    // Validate response structure
    if (!data.content || !Array.isArray(data.content) || data.content.length === 0) {
      throw new Error(`Invalid response structure: Missing content array\nResponse: ${JSON.stringify(data)}`);
    }

    if (!data.content[0]?.text) {
      throw new Error(`Invalid response structure: Missing text in first content item\nResponse: ${JSON.stringify(data)}`);
    }

    // Record usage if available in the response
    console.log('API Response:', data);
    if (data.usage) {
      console.log('Recording usage:', {
        modelId,
        usage: {
          prompt_tokens: data.usage.input_tokens,
          completion_tokens: data.usage.output_tokens,
          total_tokens: data.usage.input_tokens + data.usage.output_tokens
        }
      });
      const { user, topUpValue } = useStore.getState();
      if (user) {
        await usageService.recordUsage(
          user.uid,
          modelId,
          {
            prompt_tokens: data.usage.input_tokens,
            completion_tokens: data.usage.output_tokens,
            total_tokens: data.usage.input_tokens + data.usage.output_tokens
          },
          topUpValue
        );
      }
    } else {
      console.warn('No usage data in response:', data);
    }

    return data.content[0].text;
  } catch (error: any) {
    // Log detailed error information
    console.error('Anthropic API Error:', {
      message: error.message,
      stack: error.stack,
      cause: error.cause
    });
    
    // Categorize errors for better error messages
    if (error.message.includes('Failed to fetch')) {
      throw new Error('Network error: Failed to connect to Anthropic proxy. Please check your internet connection.');
    }
    if (error.message.includes('model:')) {
      throw new Error('Model Error: Invalid or unsupported model specified. Please check the model ID.');
    }
    if (error.message.includes('messages: Unexpected role')) {
      throw new Error('Message Format Error: Invalid message role. Please check message formatting.');
    }
    if (error.message.includes('401')) {
      throw new Error('Authentication Error: Invalid or expired API key.');
    }
    if (error.message.includes('429')) {
      throw new Error('Rate Limit Error: Too many requests. Please try again later.');
    }
    if (error.message.includes('500')) {
      throw new Error('Server Error: Anthropic proxy is experiencing issues. Please try again later.');
    }
    if (error.message.includes('parse JSON')) {
      throw new Error('Response Format Error: Received invalid response from proxy. Please try again.');
    }
    
    // If it's already an APIError, rethrow it
    if (error instanceof APIError) {
      throw error;
    }
    
    // For any other errors, wrap in APIError with detailed message
    throw new APIError(
      `Anthropic Proxy Error: ${error.message}`,
      'Anthropic'
    );
  }
}